import { useCallback, useMemo, useRef, useState } from 'react';

import {
	BOUNCY_SUMS_SCREEN_TRANSITION_MS,
	BouncySumsSceneStatus,
	BouncySumsScreen,
} from '@/features/bouncySums/constants';
import { buildBouncySumsQuestions, getBouncySumsPlaylistSectionIds } from '@/features/bouncySums/helpers';
import { fetchBouncySumsQuestions } from '@/features/bouncySums/api/questions';
import { EventBus, EVENTS } from '@/features/bouncySums/game/events/bouncySumsEventBus';
import { EndOutcome } from '@/features/gameSession/types';

import type {
	BouncySumsConfig,
	BouncySumsQuestion,
	BouncySumsQuestionsRequestEvent,
	BouncySumsSceneStateEvent,
} from '@/features/bouncySums/types';
import type { GameCloseEvent, Question } from '@/features/gameSession/types';

type UseBouncySumsGameFlowParams = {
	audioOn: boolean;
	config: BouncySumsConfig;
	onClose: (event?: GameCloseEvent) => void;
	onQuestionsRequest?: (event: BouncySumsQuestionsRequestEvent) => Promise<Question[]>;
	questions: Question[];
	sceneState: BouncySumsSceneStateEvent | null;
};

const getQuestionsForTables = (questions: BouncySumsQuestion[], tables: number[]) => {
	const selectedTableSet = new Set(tables.map(String));

	return questions.filter((question) => selectedTableSet.has(question.table));
};

export function useBouncySumsGameFlow({
	audioOn,
	config,
	onClose,
	onQuestionsRequest,
	questions,
	sceneState,
}: UseBouncySumsGameFlowParams) {
	const hasStartedRef = useRef(false);
	const startGameTimeoutRef = useRef<number | null>(null);
	const [loadedQuestions, setLoadedQuestions] = useState<Question[]>([]);
	const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
	const [selectedTables, setSelectedTables] = useState<number[]>([]);
	const [activeScreen, setActiveScreen] = useState<BouncySumsScreen>(BouncySumsScreen.START);

	const activeQuestions = loadedQuestions.length ? loadedQuestions : questions;
	const bouncyQuestions = useMemo(() => buildBouncySumsQuestions(activeQuestions), [activeQuestions]);

	const returnToStartScreen = useCallback(() => {
		if (startGameTimeoutRef.current !== null) {
			window.clearTimeout(startGameTimeoutRef.current);
			startGameTimeoutRef.current = null;
		}

		hasStartedRef.current = false;
		setActiveScreen(BouncySumsScreen.START);
		EventBus.emit(EVENTS.RETURN_TO_START);
	}, []);

	const handleBack = useCallback(() => {
		if (startGameTimeoutRef.current !== null) {
			window.clearTimeout(startGameTimeoutRef.current);
			startGameTimeoutRef.current = null;
		}

		if (activeScreen === BouncySumsScreen.GAME || sceneState?.status === BouncySumsSceneStatus.PLAYING) {
			returnToStartScreen();
			return;
		}

		onClose({
			finishEvent: {
				gameOutcome: EndOutcome.ABANDONED,
				score: sceneState?.score,
				data: {
					finished: false,
					score: sceneState?.score ?? 0,
				},
			},
		});
	}, [activeScreen, onClose, returnToStartScreen, sceneState?.score, sceneState?.status]);

	const handleTableToggle = useCallback((table: number) => {
		setSelectedTables((currentTables) => {
			if (!currentTables.includes(table)) {
				return [...currentTables, table].sort((a, b) => a - b);
			}

			return currentTables.filter((currentTable) => currentTable !== table);
		});
	}, []);

	const startSelectedTablesGame = useCallback(
		(selectedTablesForGame: number[], nextQuestions: Question[]) => {
			const nextBouncyQuestions = getQuestionsForTables(
				buildBouncySumsQuestions(nextQuestions),
				selectedTablesForGame,
			);

			setLoadedQuestions(nextQuestions);

			if (!nextBouncyQuestions.length) {
				hasStartedRef.current = false;
				setActiveScreen(BouncySumsScreen.START);
				return;
			}

			EventBus.emit(EVENTS.LOAD_GAME, {
				config,
				questions: nextBouncyQuestions,
				audioOn,
			});
			setActiveScreen(BouncySumsScreen.GAME);

			startGameTimeoutRef.current = window.setTimeout(() => {
				startGameTimeoutRef.current = null;
				EventBus.emit(EVENTS.START_GAME, selectedTablesForGame);
			}, BOUNCY_SUMS_SCREEN_TRANSITION_MS);
		},
		[audioOn, config],
	);

	const handleStartGame = useCallback(
		async (event?: { currentTarget?: EventTarget | null }) => {
			if (hasStartedRef.current) {
				return;
			}

			hasStartedRef.current = true;

			if (event?.currentTarget instanceof HTMLElement) {
				event.currentTarget.blur();
			}

			const selectedTablesForGame = selectedTables.length ? selectedTables : config.tables;
			const playlistSectionIds = getBouncySumsPlaylistSectionIds(selectedTablesForGame, config.playlistSections);

			if (!onQuestionsRequest && questions.length) {
				startSelectedTablesGame(selectedTablesForGame, questions);
				return;
			}

			setIsLoadingQuestions(true);

			try {
				const fetchedQuestions = onQuestionsRequest
					? await onQuestionsRequest({
							playlistSectionIds,
							selectedTables: selectedTablesForGame,
					  })
					: await fetchBouncySumsQuestions(playlistSectionIds);

				startSelectedTablesGame(selectedTablesForGame, fetchedQuestions);
			} catch {
				startSelectedTablesGame(selectedTablesForGame, questions);
			} finally {
				setIsLoadingQuestions(false);
			}
		},
		[
			config.playlistSections,
			config.tables,
			onQuestionsRequest,
			questions,
			selectedTables,
			startSelectedTablesGame,
		],
	);

	const handleReplayGame = useCallback(
		(nextQuestions?: Question[]) => {
			const selectedTablesForGame = selectedTables.length ? selectedTables : config.tables;

			hasStartedRef.current = true;

			if (nextQuestions?.length) {
				startSelectedTablesGame(selectedTablesForGame, nextQuestions);
				return;
			}

			EventBus.emit(EVENTS.START_GAME, selectedTablesForGame);
		},
		[config.tables, selectedTables, startSelectedTablesGame],
	);

	const handleNumpadDigit = useCallback((digit: string) => {
		EventBus.emit(EVENTS.INPUT_DIGIT, digit);
	}, []);

	return {
		activeScreen,
		bouncyQuestions,
		handleBack,
		handleNumpadDigit,
		handleReplayGame,
		handleStartGame,
		handleTableToggle,
		isLoadingQuestions,
		returnToStartScreen,
		selectedTables,
		startGameTimeoutRef,
	};
}
